import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Loader, TextButtonSize } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { ThemedTextButton } from '../../ThemedComponents';
import s from './SkipEnforcementButton.scss';

interface SkipEnforcementButtonProps {
  /** BI screen name reported with the skip click. */
  screenName: string;
  size?: TextButtonSize;
  dataHook?: string;
}

/**
 * "Skip for now" link shared by the redesigned enforcement screens. Clicking it
 * proceeds to the post-auth URL (a redirect / postMessage that takes a beat), so
 * while `store.isSkipping` is set we show a loader instead — this both gives
 * feedback and prevents a double-skip.
 *
 * The link stays mounted (kept invisible) while skipping and the loader is overlaid
 * on top, so the element keeps its exact footprint and the card doesn't jump/re-center
 * when the label is swapped for the (smaller) loader.
 */
export const SkipEnforcementButton: React.FC<SkipEnforcementButtonProps> =
  observer(({ screenName, size = 'small', dataHook }) => {
    const { t } = useTranslation();
    const {
      rootStore: {
        postLoginStore: { premiumUsers2FaEnforcementStore: store },
      },
    } = useContext(AppContextProvider);
    const { isSkipping } = store;

    return (
      <div className={s.root}>
        <span className={classNames({ [s.hidden]: isSkipping })}>
          <ThemedTextButton
            dataHook={dataHook}
            size={size}
            underline="always"
            disabled={isSkipping}
            onClick={() => store.skipEnforcement(screenName)}
          >
            {t('premium.users.2fa.enforcement.skip.cta')}
          </ThemedTextButton>
        </span>
        {isSkipping && (
          <span className={s.loaderOverlay}>
            <Loader size="tiny" />
          </span>
        )}
      </div>
    );
  });
